import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { ClinicalApproachFormValues } from "./interface";
import useGetClinicalApproach from "hooks/api/client/useGetClinicalApproach";
import useSaveClinicalApproach from "hooks/api/client/useSaveClinicalApproach";
import useGetDischargePlanning from "hooks/api/client/useGetDischargePlanning";
import useSaveDischargePlanning from "hooks/api/client/useSaveDischargePlanning";

const EMPTY_VALUES: ClinicalApproachFormValues = {
  treatmentModality: "",
  treatmentModalityRationale: "",
  generalizationPlan: "",
  maintenancePlan: "",
};

export const ClinicalApproach: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { clinicalApproach, refetchClinicalApproach, isFetched: isClinicalApproachFetched } =
    useGetClinicalApproach(clientIdNum);
  const { dischargePlanning, refetchDischargePlanning, isFetched: isDischargePlanningFetched } =
    useGetDischargePlanning(clientIdNum);

  const {
    mutateAsync: saveClinicalApproachMutationAsync,
    isSaveClinicalApproachLoading,
  } = useSaveClinicalApproach();
  const {
    mutateAsync: saveDischargePlanningMutationAsync,
    isSaveDischargePlanningLoading,
  } = useSaveDischargePlanning();

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<ClinicalApproachFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<ClinicalApproachFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (clinicalApproach || dischargePlanning) {
      const values: ClinicalApproachFormValues = {
        treatmentModality: clinicalApproach?.treatment_modality || "",
        treatmentModalityRationale: clinicalApproach?.treatment_modality_rationale || "",
        generalizationPlan: dischargePlanning?.generalization_plan || "",
        maintenancePlan: clinicalApproach?.maintenance_plan || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [clinicalApproach, dischargePlanning]);

  const handleChange =
    (field: keyof ClinicalApproachFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setFormValues((prev) => {
        if (field === "treatmentModality" && nextValue !== prev.treatmentModality) {
          return {
            ...prev,
            treatmentModality: nextValue,
            treatmentModalityRationale: "",
          };
        }

        return { ...prev, [field]: nextValue };
      });
    };

  const handleSave = async () => {
    if (!clientIdNum) return;
    const shouldSendRationale = Boolean(formValues.treatmentModality);
    const clinicalApproachPayload = {
      treatment_modality: formValues.treatmentModality || null,
      treatment_modality_rationale: shouldSendRationale
        ? formValues.treatmentModalityRationale || null
        : null,
      maintenance_plan: formValues.maintenancePlan || null,
    };
    const dischargePlanningPayload = {
      generalization_plan: formValues.generalizationPlan || null,
    };

    try {
      await Promise.all([
        saveClinicalApproachMutationAsync({
          clientId: clientIdNum,
          recordId: clinicalApproach?.an_internal_id,
          payload: clinicalApproachPayload,
        }),
        saveDischargePlanningMutationAsync({
          clientId: clientIdNum,
          recordId: dischargePlanning?.an_internal_id,
          payload: dischargePlanningPayload,
        }),
      ]);
      await Promise.all([refetchClinicalApproach(), refetchDischargePlanning()]);
      setEditMode(false);
    } catch {
      // Toasts are handled by the save hooks.
    }
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isSaveClinicalApproachLoading ||
    isSaveDischargePlanningLoading;

  if (!isClinicalApproachFetched || !isDischargePlanningFetched) {
    return (
      <SectionWrapper
        title="Clinical Approach"
        description="How services are delivered and which skill areas are prioritized"
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="50%" height={28} />
          <Skeleton variant="rounded" height={96} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Clinical Approach"
      description="How services are delivered and which skill areas are prioritized"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default ClinicalApproach;
